import React, { useState, useEffect } from 'react';
import './SettingsPage.css';
import { getRoles, createUser, testConnection, getWorkingAreas, getLocations, getUsersByRole, getVehicleTypes } from '../../api/apiClient';
import tooltipTexts from '../../utils/tooltipTexts';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('addUser');
  
  // Add User form state
  const [userName, setUserName] = useState('');
  const [workingArea, setWorkingArea] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [phoneNo, setPhoneNo] = useState('');
  const [password, setPassword] = useState('');
  const [location, setLocation] = useState('');
  const [roleId, setRoleId] = useState('');
  const [roles, setRoles] = useState([]);
  const [workingAreas, setWorkingAreas] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Give Permission form state
  const [userLocation, setUserLocation] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [equipmentPermission, setEquipmentPermission] = useState('');
  
  // Add Remarks form state
  const [remarkUserLocation, setRemarkUserLocation] = useState('');
  const [remarkSelectedUser, setRemarkSelectedUser] = useState('');
  const [remarks, setRemarks] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');

  const [locations, setLocations] = useState([]);
  const [role1Users, setRole1Users] = useState([]);

  // Add new state variables for vehicle types
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [vehicleError, setVehicleError] = useState(null);

  const [remarkRole1Users, setRemarkRole1Users] = useState([]);

  // Add state for vehicle permissions data
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [permissionsError, setPermissionsError] = useState(null);

  // Basic search state
  const [permissionSearchQuery, setPermissionSearchQuery] = useState('');

  // Get admin's location from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.locationId) {
      setLocation(user.locationId);
      setUserLocation(user.locationId);
      setRemarkUserLocation(user.locationId);
    }
  }, []);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      // Get admin's location ID from localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      const adminLocationId = user?.locationId;
      
      if (!adminLocationId) {
        console.error('No location ID found for admin');
        return;
      }
      
      // Fetch locations from database
      const locationsData = await getLocations(adminLocationId);
      console.log('Locations data:', locationsData);
      setLocations(locationsData);
      
      // Set the location in all forms
      setLocation(adminLocationId);
      setUserLocation(adminLocationId);
      setRemarkUserLocation(adminLocationId);
    };

    fetchLocations();
  }, []);

  // Check connection
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const isConnected = await testConnection();
        setConnectionStatus(isConnected);
        console.log('API connection status:', isConnected);
      } catch (error) {
        console.error('Connection test error:', error);
        setConnectionStatus(false);
      }
    };
    
    checkConnection();
  }, []);

  // Fetch roles from the database
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        console.log('Fetching roles...');
        const rolesData = await getRoles();
        console.log('Roles data received:', rolesData);
        
        // Filter out role with ID 3 (superadmin)
        const filteredRoles = rolesData ? rolesData.filter(role => role.RoleID !== 3) : [];
        setRoles(filteredRoles || []);
      } catch (error) {
        console.error('Error fetching roles:', error);
        setErrorMessage('Failed to fetch roles. Please make sure the server is running.');
      }
    };
    
    fetchRoles();
  }, []);

  // Fetch working areas from the database
  useEffect(() => {
    const fetchWorkingAreas = async () => {
      try {
        console.log('Fetching working areas...');
        // Get admin's location ID from localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        const adminLocationId = user?.locationId;
        
        const workingAreasData = await getWorkingAreas(adminLocationId);
        console.log('Working areas data received:', workingAreasData);
        setWorkingAreas(workingAreasData || []);
      } catch (error) {
        console.error('Error fetching working areas:', error);
      }
    };
    
    fetchWorkingAreas();
  }, []);

  // Fetch users with RoleId = 1 for the Give Permission dropdown
  useEffect(() => {
    const fetchRole1Users = async () => {
      // Get admin's locationId from localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      const adminLocationId = user?.locationId;
      if (!adminLocationId) {
        setRole1Users([]);
        return;
      }
      const users = await getUsersByRole(1, adminLocationId);
      setRole1Users(users);
    };
    fetchRole1Users();
  }, []);

  // Add new useEffect for fetching vehicle types when location changes
  useEffect(() => {
    if (userLocation) {
      fetchVehicleTypes(userLocation);
    }
  }, [userLocation]);

  // Modified useEffect to also fetch permissions when userLocation changes or when a permission is added
  useEffect(() => {
    if (userLocation) {
      fetchVehiclePermissions(userLocation);
    }
  }, [userLocation]);

  const fetchVehicleTypes = async (locationId) => {
    setIsLoadingVehicles(true);
    setVehicleError(null);
    setVehicleTypes([]);
    setSelectedVehicle('');
    
    try {
      console.log(`Fetching vehicles for location ID: ${locationId}`);
      const data = await getVehicleTypes(locationId);
      
      console.log(`Received vehicles for location ${locationId}:`, data);
      
      if (data.length === 0) {
        const selectedLocationName = locations.find(loc => 
          String(loc.LocationID) === String(locationId)
        )?.LocationName || 'Selected location';
        
        setVehicleError(`${selectedLocationName} has no registered vehicles`);
      } else {
        setVehicleTypes(data);
      }
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      setVehicleError('Failed to load vehicles for this location');
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  const fetchVehiclePermissions = async (locationId) => {
    setLoadingPermissions(true);
    setPermissionsError(null);
    try {
      const response = await fetch(`http://eframeehs.in:3001/api/vehicle-permissions?locationId=${locationId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch vehicle permissions');
      }
      
      const data = await response.json();
      setPermissions(data);
    } catch (error) {
      console.error('Error fetching vehicle permissions:', error);
      setPermissionsError('Failed to load vehicle permissions');
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Log form data
      console.log('Form Data being submitted:', {
        userName,
        workingArea,
        employeeId,
        phoneNo,
        password,
        location,
        roleId,
        photo: photo ? photo.name : null
      });
      
      // Convert photo to base64 if needed
      let photoBase64 = null;
      if (photo) {
        try {
          photoBase64 = await readFileAsBase64(photo);
          console.log('Photo converted to base64 successfully');
        } catch (photoError) {
          console.error('Error converting photo:', photoError);
        }
      }
      
      // Prepare user data for API
      const userData = {
        userName,
        workingArea,
        employeeId,
        phoneNo,
        password,
        location,
        roleId,
        photo: photoBase64
      };
      
      console.log('About to call createUser API with data:', { ...userData, photo: userData.photo ? 'base64_data' : null });
      // Submit to API
      const result = await createUser(userData);
      console.log('API Response:', result);
      
      // Reset form on success
      setUserName('');
      setWorkingArea('');
      setEmployeeId('');
      setPhoto(null);
      setPhotoPreview(null);
      setPhoneNo('');
      setPassword('');
      setLocation('');
      setRoleId('');
      
      setSuccessMessage('User created successfully');
    } catch (error) {
      console.error('Error in form submission:', error);
      setErrorMessage(error.message || 'Failed to create user. Please check the server connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const readFileAsBase64 = (file) => {
    console.log('Reading file:', file.name);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(',')[1]; // remove data URL prefix
        console.log('File converted to base64 (truncated):', base64String?.substring(0, 20) + '...');
        resolve(base64String);
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        reject(error);
      };
    });
  };

  const handleGivePermissionSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !selectedVehicle) {
      console.error('Please select both user and vehicle');
      return;
    }

    try {
      // Prepare the data for API
      const permissionData = {
        userPK: selectedUser,
        vehicleId: selectedVehicle,
        locationId: userLocation
      };

      // Call your API endpoint to save the permission
      const response = await fetch('http://eframeehs.in:3001/api/give-vehicle-permission', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(permissionData),
      });

      if (!response.ok) {
        throw new Error('Failed to save vehicle permission');
      }

      // Reset form
      setSelectedUser('');
      setSelectedVehicle('');
      
      // Refresh permissions list
      fetchVehiclePermissions(userLocation);
      
      // Show success message (you'll need to add state for this)
      alert('Vehicle permission updated successfully');
      
    } catch (error) {
      console.error('Error saving vehicle permission:', error);
      alert('Failed to update vehicle permission. Please try again.');
    }
  };

  const handleAddRemarksSubmit = async (e) => {
    e.preventDefault();
    
    if (!remarkSelectedUser || remarks.trim() === '') {
      alert('Please select a user and enter remarks');
      return;
    }

    try {
      const response = await fetch('http://eframeehs.in:3001/api/update-vehicle-remarks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userPK: remarkSelectedUser,
          remarks: remarks.trim()
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update remarks');
      }

      // Reset form
      setRemarkSelectedUser('');
      setRemarks('');
      
      // Show success message
      alert('Remarks updated successfully');
      
    } catch (error) {
      console.error('Error updating remarks:', error);
      alert('Failed to update remarks. Please try again.');
    }
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Check file size - 50KB = 51200 bytes
      if (selectedFile.size > 51200) {
        setErrorMessage('Photo size exceeds 50KB limit. Please select a smaller image.');
        return;
      }
      
      setPhoto(selectedFile);
      
      // Create a preview URL for the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const clearPhotoSelection = () => {
    setPhoto(null);
    setPhotoPreview(null);
    // Reset the file input value
    const fileInput = document.getElementById('photo-upload');
    if (fileInput) fileInput.value = '';
  };

  useEffect(() => {
    const fetchRemarkRole1Users = async () => {
      const user = JSON.parse(localStorage.getItem('user'));
      const adminLocationId = user?.locationId;
      if (!adminLocationId) {
        setRemarkRole1Users([]);
        return;
      }
      const users = await getUsersByRole(1, adminLocationId);
      setRemarkRole1Users(users);
    };
    fetchRemarkRole1Users();
  }, []);

  // Table component for permissions
  const renderPermissionsTable = () => {
    if (loadingPermissions) {
      return <div className="loading-message">Loading permissions...</div>;
    }

    if (permissionsError) {
      return <div className="error-message">{permissionsError}</div>;
    }

    if (!permissions || permissions.length === 0) {
      return <div className="no-data-message">No permissions found for this location.</div>;
    }

    // Filter permissions based on search query
    const filteredPermissions = permissions.filter(permission => 
      permission.EmployeeName.toLowerCase().includes(permissionSearchQuery.toLowerCase()) ||
      permission.ScreenName.toLowerCase().includes(permissionSearchQuery.toLowerCase())
    );

    return (
      <div className="permissions-table-container">
        <h3>Existing Equipment Permissions</h3>
        
        {/* Search input */}
        <div className="permissions-search-container">
          <input
            type="text"
            className="permissions-search-input"
            placeholder="Quick search by employee or vehicle name..."
            value={permissionSearchQuery}
            onChange={(e) => setPermissionSearchQuery(e.target.value)}
            title={tooltipTexts.equipmentPermissionsSearch}
          />
        </div>
        
        {filteredPermissions.length === 0 ? (
          <div className="no-data-message">No matches found for your search.</div>
        ) : (
          <table className="permissions-table">
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Vehicle Name</th>
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.map((permission, index) => (
                <tr key={`${permission.UserPK}-${permission.VehicleID}-${index}`}>
                  <td>{permission.EmployeeName}</td>
                  <td>{permission.ScreenName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="settings-page">
      <div className="content-area">
        <div className="settings-header rounded-none">
          <h2>
            Settings
            <span className="header-tooltip">
              <span className="info-icon">i</span>
              <span className="tooltip-text">{tooltipTexts.settingsHeader}</span>
            </span>
          </h2>
          {connectionStatus === false && (
            <div className="connection-error">
              Server connection failed. Please make sure the backend server is running at {API_BASE_URL}.
            </div>
          )}
        </div>
        
        <div className="settings-tabs">
          <button 
            className={`tab-button ${activeTab === 'addUser' ? 'active' : ''}`}
            onClick={() => setActiveTab('addUser')}
          >
            Add User
          </button>
          <button 
            className={`tab-button ${activeTab === 'givePermission' ? 'active' : ''}`}
            onClick={() => setActiveTab('givePermission')}
          >
            Give Mobile Equipment Permission to User
          </button>
          <button 
            className={`tab-button ${activeTab === 'addRemarks' ? 'active' : ''}`}
            onClick={() => setActiveTab('addRemarks')}
          >
            Add Remarks to User
          </button>
        </div>
        
        {activeTab === 'addUser' && (
          <div className="add-user-form tab-content-section">
            {errorMessage && <div className="error-message">{errorMessage}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
            {roles.length === 0 && (
              <div className="warning-message">
                No roles were found. Please make sure the backend server is running and the roles table is populated.
              </div>
            )}
            
            <form onSubmit={handleAddUserSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name</label>
                  <input 
                    type="text" 
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Working Area</label>
                  <select
                    value={workingArea}
                    onChange={(e) => setWorkingArea(e.target.value)}
                    required
                  >
                    <option value="">Select Working Area</option>
                    {workingAreas && workingAreas.length > 0 ? (
                      workingAreas.map((area) => (
                        <option key={area.GroupID} value={area.GroupID}>
                          {area.WorkingAreaName}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No working areas available</option>
                    )}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Employee ID</label>
                  <input 
                    type="text" 
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    required
                    placeholder="This will be used as log in ID"
                    className="employee-id-input"
                  />
                </div>
                <div className="form-group">
                  <label>Profile Photo:</label>
                  <div className="photo-upload">
                    {photoPreview && (
                      <div className="photo-preview-container">
                        <div className="photo-preview">
                          <img src={photoPreview} alt="Profile preview" />
                        </div>
                        <button 
                          type="button" 
                          className="remove-photo-btn" 
                          onClick={clearPhotoSelection}
                          title="Remove photo"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    <label className="photo-label">
                      {photoPreview ? 'Change Photo' : 'Upload Photo'}
                      <input
                        type="file"
                        className="photo-input"
                        accept="image/*"
                        onChange={handlePhotoChange}
                      />
                    </label>
                  </div>
                  <div className="photo-size-note">Photo size should not exceed 50KB</div>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Phone No</label>
                  <input 
                    type="tel" 
                    value={phoneNo}
                    onChange={(e) => setPhoneNo(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input 
                    type="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Location</label>
                  <select
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    required
                    disabled={!!location}
                  >
                    <option value="">Select Location</option>
                    {locations.map(loc => (
                      <option 
                        key={loc.LocationID} 
                        value={loc.LocationID}
                      >
                        {loc.LocationName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                    required
                  >
                    <option value="">Select Role</option>
                    {roles && roles.length > 0 ? (
                      roles.map((role) => (
                        <option key={role.RoleID} value={role.RoleID}>
                          {role.Description}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No roles available</option>
                    )}
                  </select>
                </div>
              </div>
              
              <div className="form-submit">
                <button 
                  type="submit" 
                  className="submit-button" 
                  disabled={isSubmitting || connectionStatus === false}
                >
                  {isSubmitting ? 'Signing Up...' : 'Sign Up'}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {activeTab === 'givePermission' && (
          <div className="give-permission-form tab-content-section">
            <form onSubmit={handleGivePermissionSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>User Location:</label>
                  <select
                    value={userLocation}
                    onChange={(e) => setUserLocation(e.target.value)}
                    required
                    disabled={!!location}
                  >
                    <option value="">Select Location*</option>
                    {locations.map(loc => (
                      <option 
                        key={loc.LocationID} 
                        value={loc.LocationID}
                      >
                        {loc.LocationName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Select User:</label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    required
                  >
                    <option value="">Select User*</option>
                    {role1Users.map(user => (
                      <option key={user.UserPK} value={user.UserPK}>
                        {user.EmployeeName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Select Mobile Equipment:</label>
                  <select
                    value={selectedVehicle}
                    onChange={(e) => setSelectedVehicle(e.target.value)}
                    className="equipment-select"
                    disabled={isLoadingVehicles || !userLocation || vehicleError !== null}
                    required
                  >
                    {isLoadingVehicles ? (
                      <option value="">Loading equipment types...</option>
                    ) : !userLocation ? (
                      <option value="">Select Location First</option>
                    ) : vehicleError ? (
                      <option value="">{vehicleError}</option>
                    ) : vehicleTypes.length === 0 ? (
                      <option value="">No equipment types available</option>
                    ) : (
                      <>
                        <option value="">Select Mobile Equipment Type*</option>
                        {vehicleTypes.map(vehicle => (
                          <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                            {vehicle.ScreenName || vehicle.VehicleName || `Vehicle ${vehicle.VehicleID}`}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {vehicleError && (
                    <div className="error-message equipment-error">{vehicleError}</div>
                  )}
                </div>
              </div>
              
              <div className="form-submit">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={!selectedUser || !selectedVehicle || isLoadingVehicles}
                >
                  Submit
                </button>
              </div>
            </form>
            
            {userLocation && renderPermissionsTable()}
          </div>
        )}
        
        {activeTab === 'addRemarks' && (
          <div className="add-remarks-form tab-content-section">
            <form onSubmit={handleAddRemarksSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>User Location:</label>
                  <select
                    value={remarkUserLocation}
                    onChange={(e) => setRemarkUserLocation(e.target.value)}
                    required
                    disabled={!!location}
                  >
                    <option value="">Select Location*</option>
                    {locations.map(loc => (
                      <option 
                        key={loc.LocationID} 
                        value={loc.LocationID}
                      >
                        {loc.LocationName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Select User:</label>
                  <select
                    value={remarkSelectedUser}
                    onChange={(e) => setRemarkSelectedUser(e.target.value)}
                    required
                  >
                    <option value="">Select User*</option>
                    {remarkRole1Users.map(user => (
                      <option key={user.UserPK} value={user.UserPK}>
                        {user.EmployeeName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Enter Remarks:</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    required
                    rows={5}
                  />
                </div>
              </div>
              
              <div className="form-submit">
                <button type="submit" className="submit-button">
                  Submit
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

// Add this for convenience
const API_BASE_URL = 'http://eframeehs.in:3001';

export default SettingsPage; 